import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { BiCountryId } from "@globals/enums/BiLanguageAndCountryId";
import { cloneObject, removeItemAtImmutable } from "@globals/helper-functions";
import { EnrollmentAddressDto } from "@globals/openapi-models/model/enrollmentAddressDto";
import { EnrollmentInitialDataDto } from "@globals/openapi-models/model/enrollmentInitialDataDto";
import { EnrollmentsDto } from "@globals/openapi-models/model/enrollmentsDto";
import { OperatorInfoDto } from "@globals/openapi-models/model/operatorInfoDto";
import { SignUpEnrolleeDtoExt } from "@models-sub/Ext/SignUpEnrolleeDtoExt";
import { SenderDto } from "@models-sub/SenderDto";
import { ApiRoutesEn } from "@shared-sub/ApiRoutesEn";

import { BehaviorSubject, tap } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class EnrollmentService {
  private enrollmentsData = new BehaviorSubject<EnrollmentsDto>(undefined);
  public myAddressesModel$ = this.enrollmentsData.asObservable();
  public accessToken: string;

  private readonly http = inject(HttpClient);

  public getAddresses(countryId: BiCountryId, zipCode: number, streetId: number) {
    const params = {
      countryId,
      zipCode,
      streetId
    };
    return this.http.get<EnrollmentAddressDto[]>(ApiRoutesEn.enrollmentRoutes.get.getAddresses, { params: params });
  }

  /**
   * Requests the country ID based on the host name (as we cannot be sure about that here on client side).
   */
  public getInitialData() {
    return this.http.get<EnrollmentInitialDataDto>(ApiRoutesEn.enrollmentRoutes.get.getInitialData).pipe(
      tap(data => {
        this.accessToken = data.accessToken;
      })
    );
  }

  public getOperatorInfo(countryId: BiCountryId) {
    return this.http.get<OperatorInfoDto>(ApiRoutesEn.enrollmentRoutes.get.getOperatorInfo, {
      params: { countryId }
    });
  }

  public getSenderBySenderId(senderId: string) {
    const params: { [key: string]: string } = {
      senderId
    };
    return this.http.get<SenderDto>(ApiRoutesEn.enrollmentRoutes.get.getSenderBySenderId, { params: params });
  }

  public getSenderBySlug(slug: string) {
    const params = { slug };

    return this.http.get<SenderDto>(ApiRoutesEn.enrollmentRoutes.get.getSenderBySlug, { params });
  }

  /**
   *  Returns model to be used for My addresses/main page where multiple data is needed
   */
  public initEnrollmentsData() {
    return this.http.get<EnrollmentsDto>(ApiRoutesEn.enrollmentRoutes.get.getEnrollments).pipe(
      tap(model => {
        this.enrollmentsData.next(model);
      })
    );
  }

  public clearEnrollmentsData() {
    this.enrollmentsData.next(undefined);
  }

  public addEnrolleeAddress(enrolleeAddress: EnrollmentAddressDto) {
    const dto = {
      locationName: enrolleeAddress.locationName ? enrolleeAddress.locationName : "",
      kvhx: enrolleeAddress.kvhx,
      countryId: enrolleeAddress.countryId
    };

    return this.http.post(ApiRoutesEn.enrollmentRoutes.addEnrolleeAddress, dto).pipe(
      tap(() => {
        this.enrollmentsData.next({
          enrolleeInfo: this.enrollmentsData.value.enrolleeInfo,
          enrollmentAddresses: [...this.enrollmentsData.value.enrollmentAddresses, enrolleeAddress]
        });
      })
    );
  }

  public deleteEnrolleeAddress(enrolleeAddressKvhx: string) {
    return this.http
      .delete(ApiRoutesEn.enrollmentRoutes.delete.deleteEnrolleeAddress, {
        params: { kvhx: enrolleeAddressKvhx }
      })
      .pipe(
        tap(() => {
          const index = this.enrollmentsData.value.enrollmentAddresses.findIndex(address => address.kvhx === enrolleeAddressKvhx);
          if (index > -1) {
            this.enrollmentsData.next({
              enrolleeInfo: this.enrollmentsData.value.enrolleeInfo,
              enrollmentAddresses: removeItemAtImmutable(this.enrollmentsData.value.enrollmentAddresses, index)
            });
          }
        })
      );
  }

  /**
   * Updates only the name of a enrollee address
   */
  public updateEnrolleeAddressName(newName: string, kvhx: string) {
    return this.http.patch(ApiRoutesEn.enrollmentRoutes.update.updateEnrolleeAddressName, {}, { params: { newName, kvhx } }).pipe(
      tap(() => {
        const stateClone = cloneObject(this.enrollmentsData.value);
        const address = stateClone.enrollmentAddresses.find(address => address.kvhx === kvhx);
        if (address) {
          address.locationName = newName;
          this.enrollmentsData.next(stateClone);
        }
      })
    );
  }

  public signUp(signUpInfo: SignUpEnrolleeDtoExt) {
    return this.http.post(ApiRoutesEn.enrollmentRoutes.signUp, signUpInfo);
  }
}
